import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import { personalKbApi, type FeishuNode, PersonalKbError } from "../lib/personalKbApi";

/**
 * Feishu doc picker (personal-materials.md Stage 4b). The user browses their OWN
 * Feishu Drive (folder tree) and checks the docs to add to 我的资料 — the
 * "user self-filters" control over Stage 4a's broad live search. Picked docs are
 * read + ingested server-side, then behave like any uploaded file (auto-used +
 * provenance). Folders are navigable; only docx are selectable.
 */
export function FeishuPicker({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  /** Called after docs are added so the caller can refresh the library. */
  onAdded?: () => void;
}): JSX.Element {
  // Breadcrumb stack of folders navigated into (root = empty).
  const [trail, setTrail] = useState<{ token: string; name: string }[]>([]);
  const [nodes, setNodes] = useState<FeishuNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPage, setNextPage] = useState<string | undefined>(undefined);
  const [err, setErr] = useState("");
  // token → picked doc (name for the title, url for the provenance link-back).
  const [picked, setPicked] = useState<Record<string, { name: string; url: string }>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const current = trail[trail.length - 1];

  // Load a folder's first page (append=false resets) or the next page (append).
  const load = useCallback(async (folderToken: string | undefined, pageToken?: string) => {
    const append = pageToken !== undefined;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setErr("");
    try {
      const page = await personalKbApi.feishuBrowse(folderToken, pageToken);
      setNodes((prev) => (append ? [...prev, ...page.files] : page.files));
      setNextPage(page.hasMore ? page.nextPageToken : undefined);
    } catch (e) {
      setErr(e instanceof PersonalKbError ? e.message : "打不开飞书文档。");
      if (!append) setNodes([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Reload the first page whenever the folder changes (breadcrumb nav).
  useEffect(() => {
    void load(current?.token);
  }, [load, current?.token]);

  function openFolder(n: FeishuNode): void {
    setTrail((t) => [...t, { token: n.token, name: n.name }]);
  }
  function goTo(index: number): void {
    // index -1 = root; otherwise slice the trail to that crumb.
    setTrail((t) => (index < 0 ? [] : t.slice(0, index + 1)));
  }
  function toggle(n: FeishuNode): void {
    setPicked((p) => {
      const next = { ...p };
      if (next[n.token] !== undefined) delete next[n.token];
      else next[n.token] = { name: n.name, url: n.url };
      return next;
    });
  }

  // Shared submit: ingest `docs`, show ✓, refresh, auto-close.
  async function submit(docs: { token: string; name: string; url?: string }[]): Promise<void> {
    if (docs.length === 0) return;
    setBusy(true);
    setNote("");
    setErr("");
    try {
      const { files, skipped } = await personalKbApi.feishuPick(docs);
      const extra = skipped.length > 0 ? `（跳过 ${skipped.length} 个空文档）` : "";
      setNote(`已加入 ${files.length} 份，答题时自动用${extra}`);
      setPicked({});
      onAdded?.();
      // Industry pattern: a primary "add" confirms + dismisses — briefly show the
      // ✓ then auto-close, instead of making the user hunt for the X.
      setTimeout(() => onClose(), 900);
    } catch (e) {
      setErr(e instanceof PersonalKbError ? e.message : "加入没成功，稍后再试。");
      setBusy(false);
    }
  }

  async function add(): Promise<void> {
    await submit(Object.entries(picked).map(([token, { name, url }]) => ({ token, name, url })));
  }

  // Add EVERY docx in the current folder — page through all of it, then ingest.
  async function addWholeFolder(): Promise<void> {
    if (current === undefined) return;
    setBusy(true);
    setErr("");
    try {
      const docs: { token: string; name: string; url: string }[] = [];
      let cursor: string | undefined;
      do {
        const page = await personalKbApi.feishuBrowse(current.token, cursor);
        for (const f of page.files)
          if (f.type === "docx") docs.push({ token: f.token, name: f.name, url: f.url });
        cursor = page.hasMore ? page.nextPageToken : undefined;
      } while (cursor !== undefined && docs.length < 200); // hard cap: never ingest unbounded
      if (docs.length === 0) {
        setErr("这个文件夹里没有可加入的文档。");
        setBusy(false);
        return;
      }
      await submit(docs);
    } catch (e) {
      setErr(e instanceof PersonalKbError ? e.message : "加入没成功，稍后再试。");
      setBusy(false);
    }
  }

  const pickedCount = Object.keys(picked).length;

  return (
    <div className="pkb-modal-bg" onMouseDown={onClose}>
      <div className="pkb-modal fp-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="选飞书文档">
        <div className="pkb-head">
          <h2>从飞书选文档</h2>
          <button className="pkb-x" type="button" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="pkb-body">
          <p className="pkb-reassure">
            挑你想让小环用的飞书文档，加入「我的资料」后答题时自动参考。只属于你、随时可删。
          </p>

          {/* Breadcrumb */}
          <div className="fp-crumbs" data-testid="fp-crumbs">
            <button type="button" className="fp-crumb" onClick={() => goTo(-1)}>
              我的飞书
            </button>
            {trail.map((c, i) => (
              <span key={c.token}>
                <span className="fp-sep">/</span>
                <button type="button" className="fp-crumb" onClick={() => goTo(i)}>
                  {c.name}
                </button>
              </span>
            ))}
          </div>

          {err !== "" && (
            <div className="pkb-err" role="alert">
              {err}
            </div>
          )}
          {note !== "" && (
            <div className="rm-note-ok" data-testid="fp-added">
              ✓ {note}
            </div>
          )}

          <div className="fp-list" data-testid="fp-list">
            {loading ? (
              <div className="fp-placeholder">加载中…</div>
            ) : nodes.length === 0 ? (
              <div className="fp-placeholder">这个位置没有可用的文档。</div>
            ) : (
              nodes.map((n) =>
                n.type === "folder" ? (
                  <button key={n.token} type="button" className="fp-row fp-folder" onClick={() => openFolder(n)}>
                    <span className="fp-ic">📁</span>
                    <span className="fp-name">{n.name}</span>
                    <span className="fp-arrow" aria-hidden="true">›</span>
                  </button>
                ) : (
                  <label key={n.token} className="fp-row fp-doc">
                    <input
                      type="checkbox"
                      checked={picked[n.token] !== undefined}
                      onChange={() => toggle(n)}
                      data-testid="fp-doc-check"
                    />
                    <span className="fp-ic">📄</span>
                    <span className="fp-name">{n.name}</span>
                  </label>
                ),
              )
            )}
            {nextPage !== undefined && (
              <button
                type="button"
                className="fp-more"
                disabled={loadingMore}
                onClick={() => void load(current?.token, nextPage)}
                data-testid="fp-more"
              >
                {loadingMore ? "加载中…" : "加载更多"}
              </button>
            )}
          </div>
        </div>
        <div className="fp-foot">
          {current !== undefined ? (
            <button
              className="fp-folder-add"
              type="button"
              disabled={busy}
              onClick={() => void addWholeFolder()}
              data-testid="fp-add-folder"
            >
              加入整个「{current.name}」
            </button>
          ) : (
            <span className="fp-count">{pickedCount > 0 ? `已选 ${pickedCount} 份` : "勾选要加入的文档"}</span>
          )}
          <button
            className="pkb-upload"
            type="button"
            disabled={busy || pickedCount === 0}
            onClick={() => void add()}
            data-testid="fp-add"
          >
            {busy ? "加入中…" : `加入我的资料${pickedCount > 0 ? ` (${pickedCount})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
